import React, { useState, useEffect, useRef, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { Typography, Button } from '@hpi-sox/hpstellar-core/visId';
import capitalize from 'lodash/capitalize';
import { useWindowSize } from '@hpi-sox/hpstellar-core/hooks';

import SearchPageGiftCardLink from './search-page-gift-card-link';
import VisIDSortBar from '../../shared/components/visId/sort-bar';

import './css/search-page-sort-section.less';

const SortBarResultsMessage = props => {
    const { searchKey, resultsCount, currentResultsCount, isGiftCard, giftCardLabel, totalPageDisplayed, filterCount } =
        props || {};
    const [isMultiLine, setIsMultiLine] = useState(false);
    const { innerWidth } = useWindowSize();
    const isMobileVisId = useSelector(
        state =>
            state &&
            state.ui &&
            state.ui.customBreakpoints &&
            ['xsmall', 'small'].includes(state.ui.customBreakpoints.visId),
    );
    const containerRef = useRef();
    const topRef = useRef();
    const count =
        typeof resultsCount === 'number'
            ? resultsCount
            : typeof currentResultsCount === 'number'
              ? currentResultsCount
              : null;
    const hasKeyword = typeof searchKey === 'string' && searchKey.trim().length > 0;
    const resultTextPrefix =
        filterCount > 0 && !isNaN(totalPageDisplayed) && typeof totalPageDisplayed === 'number'
            ? `${totalPageDisplayed} of `
            : '';
    const resultsText = `${resultTextPrefix}${typeof resultsCount === 'number' ? resultsCount + ' ' : ''}Result${count && count > 1 ? 's' : ''}${hasKeyword ? ' for' : ''}`;

    useEffect(() => {
        try {
            const isMultiLineText = containerRef.current.getBoundingClientRect().height > 40;
            if (!isMultiLine && isMultiLineText) {
                setIsMultiLine(true);
            } else if (isMultiLine && !isMultiLineText) {
                setIsMultiLine(false);
            }
        } catch (e) {}
    }, [innerWidth, isGiftCard]);

    return (
        <>
            <span className="srp-sort-bar-top" ref={topRef} />
            <span
                ref={containerRef}
                className={`srp-results-text-container${isMultiLine ? ' srp-results-text-multi-line' : ''}`}
            >
                <span className="srp-results-text">
                    <Typography
                        variant={isMobileVisId ? 'caption' : 'body1'}
                        tag="span"
                        className="srp-sort-bar-results-text"
                    >
                        {resultsText}
                    </Typography>
                    {hasKeyword && (
                        <Typography
                            variant={isMobileVisId ? 'caption' : 'body2'}
                            tag="span"
                            className="srp-sort-bar-kw-text"
                            style={{ fontWeight: 700, marginLeft: '4px' }}
                        >
                            {capitalize(searchKey)}
                        </Typography>
                    )}
                </span>
                <SearchPageGiftCardLink
                    searchKey={searchKey}
                    validGiftCardSearch={isGiftCard}
                    giftCardLabel={giftCardLabel}
                />
            </span>
        </>
    );
};

export default props => {
    const {
        clearFilters,
        sortingOptions,
        setModalState,
        filterCount,
        selectedSortOption,
        onSort,
        resultCount,
        searchKey,
        currentResultsCount,
        totalPageDisplayed,
        zeroResults,
    } = props;

    const isMobile = useSelector(
        state => state && state.ui && state.ui.customBreakpoints && state.ui.customBreakpoints.srp === 'mobile',
    );

    const supportGtmProps = {
        'data-gtm-id': 'seach',
        'data-gtm-value': 'support',
        'data-gtm-category': 'linkClick',
    };

    const sortGtmActions = useMemo(
        () =>
            new Map([
                [
                    'dropdownSelect',
                    {
                        gtmCategory: 'facet',
                        gtmId: 'sort',
                    },
                ],
            ]),
        [],
    );

    const sortMobileProps = {
        onClick: () => setModalState(true),
        className: 'srp-mobile-filter-button',
        size: 'small',
    };

    const validSearchKey = typeof searchKey === 'string';
    const isGiftCard = validSearchKey && /^gift card/gi.test(searchKey);
    const giftCardLabel = "Check Your Gift Card's Balance";
    const evaluatedLabelLength = isGiftCard
        ? searchKey.length + giftCardLabel.length
        : validSearchKey
          ? searchKey.length
          : 0;
    const longText = evaluatedLabelLength > 120;
    const zeroResultsWithoutFilters = zeroResults && filterCount < 1;
    const className = `${longText ? ' srpLongKw' : ''}${isGiftCard ? ' srpGiftCard' : ''}${ zeroResultsWithoutFilters ? ' srpSortBarNoFilterZeroResults' : ''}`;
    return (
        <div id="srpStickyBar">
            <VisIDSortBar
                sortValues={sortingOptions}
                onSort={onSort}
                orderBy={selectedSortOption}
                totalProducts={resultCount}
                filterCount={!isMobile ? filterCount : null}
                clear={clearFilters}
                sortGtmActions={sortGtmActions}
                isCustomMobile={isMobile}
                resultsMessage={
                    <SortBarResultsMessage
                        searchKey={searchKey}
                        resultsCount={resultCount}
                        currentResultsCount={currentResultsCount}
                        filterCount={filterCount}
                        giftCardLabel={giftCardLabel}
                        isGiftCard={isGiftCard}
                        totalPageDisplayed={totalPageDisplayed}
                    />
                }
                sortMobileProps={sortMobileProps}
                disableSort={zeroResults}
                disableFilters={zeroResultsWithoutFilters}
                className={className.length > 0 ? className : ''}
                // TODO: add analytics for support click
                support={
                    <Button
                        variation={ zeroResults ? "tertiary" : "secondary" }
                        theme="light"
                        size="small"
                        className={`supportButton${zeroResults ? ' zeroResultsSupportButton' : ''}`}
                        to={`https://support.hp.com/us-en/search${searchKey ? `?q=${searchKey}` : ''}`}
                        {...supportGtmProps}
                    >
                        Go to support
                    </Button>
                }
            />
        </div>
    );
};
